import { Button } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { PageLayout } from '../../components/ui/PageLayout';
import { SkeletonCards } from '../../components/ui/Skeleton';
import { ICONS } from '../../components/ui/icons';
import './AiSkillsPage.css';

interface Template { name: string; slug: string; description: string }

/** IA › Skills › Galeria: pontos de partida de conhecimento para adaptar à empresa (página própria, não modal). */
export function AiSkillsGalleryPage() {
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [have, setHave] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [t, s] = await Promise.all([fetchApi('/api/ai/skills/templates'), fetchApi('/api/ai/skills')]);
      if (t.ok) setTemplates((await t.json()).templates ?? []); else setTemplates([]);
      if (s.ok) setHave(new Set(((await s.json()).skills ?? []).map((x: { slug: string }) => x.slug)));
    })();
  }, [fetchApi]);

  const add = async (t: Template) => {
    setBusy(t.slug);
    const r = await fetchApi('/api/ai/skills', { method: 'POST', body: JSON.stringify({ template: t.slug }) });
    const j = await r.json().catch(() => ({}));
    setBusy(null);
    if (!r.ok) { toast.error(j.message || 'Não foi possível adicionar'); return; }
    toast.success(`Skill "${t.name}" adicionada`);
    setHave((h) => new Set(h).add(t.slug));
  };

  return (
    <PageLayout
      title="Galeria de conhecimento"
      description="Pontos de partida para adaptar à sua empresa. Depois de adicionar, edite o texto em Skills."
      actions={<Button variant="secondary" icon={ICONS.ArrowLeft} onClick={() => navigate('/ia/skills')}>Voltar às skills</Button>}
    >
      {!templates && <SkeletonCards count={6} />}
      {templates && templates.length === 0 && <p className="text-kumo-subtle">Nenhum modelo disponível.</p>}
      {templates && templates.length > 0 && (
        <div className="skills-gallery !mt-0">
          {templates.map((t) => (
            <section key={t.slug} className="brand-card space-y-2">
              <div className="font-semibold">{t.name}</div>
              <div className="text-sm !text-[var(--text-muted)]">{t.description}</div>
              <Button variant="secondary" disabled={!canEdit || have.has(t.slug) || busy === t.slug} loading={busy === t.slug} onClick={() => add(t)}>
                {have.has(t.slug) ? 'Já adicionada' : 'Adicionar'}
              </Button>
            </section>
          ))}
        </div>
      )}
    </PageLayout>
  );
}
